import {GatsbyClient} from '@commercial-website/data-layer/client'
import type {Locale} from '@commercial-website/data-layer/locale'
import {locales} from '@commercial-website/data-layer/locale'
import {LogoHorizontal} from '@commercial-website/icons'
import {Footer as UIFooter} from '@commercial-website/ui'
import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import LocaleSwitcher from '@/components/locale-switcher'
import PrismicLink from '@/components/prismic-link'
import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import RichText, {hasRichText} from '@/components/rich-text'
import type {Props as RichTextProps} from '@/components/rich-text'
import {socialLinks} from '@/config/site-config'
import type {FooterQuery} from '@/graphql-types'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {useLocale} from '@/providers/locale'
import replacePlaceholders from '@/utils/replace-placeholders'

type Link = {
    text: string
    link: PrismicLinkProps
}

type FooterContent = {
    entity: string
    address: RichTextProps['content']
    terms_body: string
    copyright_text: string
    financial_partner_terms_text: RichTextProps['content']
    financial_partner_copyright_text: string
    product_heading: string
    resources_heading: string
    company_heading: string
    legal_heading: string
    partnerships_heading: string
    solutions_heading: string
    product_links: Link[]
    resources_links: Link[]
    company_links: Link[]
    legal_links: Link[]
    partnerships_links: Link[]
    solutions_links: Link[]
}

export type FooterProps = {
    locales: Locale[]
    hideLinks?: boolean
    content: FooterContent
}

const currentYear = new Date().getFullYear()

function mapLinkGroups(content: FooterContent, hideLinks: boolean) {
    if (hideLinks) {
        return []
    }

    const groups: Array<{heading: string; links: Link[]}> = [
        {heading: content.product_heading, links: content.product_links},
        {heading: content.company_heading, links: content.company_links},
        {heading: content.partnerships_heading, links: content.partnerships_links},
        {heading: content.resources_heading, links: content.resources_links},
        {heading: content.solutions_heading, links: content.solutions_links},
        {heading: content.legal_heading, links: content.legal_links},
    ]

    return groups
        .filter(({links}) => links.length > 0)
        .map(({heading, links}) => ({
            heading,
            links: links.map((item) => ({
                text: item.text,
                render: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
                    <PrismicLink {...item.link} className={props.className}>
                        {props.children}
                    </PrismicLink>
                ),
            })),
        }))
}

export const Footer = ({content, locales: footerLocales, hideLinks = false}: FooterProps) => {
    const copyright = `${content.entity}, ${currentYear}. ${content.copyright_text}`
    const financialPartnerCopyright = content.financial_partner_copyright_text
        ? replacePlaceholders(content.financial_partner_copyright_text, {
              '{{currentYear}}': `${currentYear}`,
          }).join('')
        : undefined

    return (
        <UIFooter
            logo={LogoHorizontal}
            slots={{
                localeSwitcher: <LocaleSwitcher locales={footerLocales} light />,
                address: (
                    <>
                        <p>{content.entity}</p>
                        <br />
                        <RichText $variant="paragraph-base" content={content.address} />
                    </>
                ),
                financialPartnerTerms: hasRichText(content.financial_partner_terms_text) ? (
                    <RichText content={content.financial_partner_terms_text} />
                ) : undefined,
            }}
            linkGroups={mapLinkGroups(content, hideLinks)}
            socialLinks={socialLinks}
            terms={content.terms_body}
            copyright={copyright}
            financialPartnerCopyright={financialPartnerCopyright as string | undefined}
        />
    )
}

type Props = React.ComponentPropsWithoutRef<'footer'> & {
    hideLinks?: boolean
}

const FooterWithQuery = ({hideLinks}: Props) => {
    const {allPrismicFooter} = useStaticQuery<FooterQuery>(query)
    const [prismicContent] = useLocalizedContent(allPrismicFooter.nodes)
    const currentLocale = useLocale()

    const content =
        currentLocale.code === 'en-us'
            ? GatsbyClient.getFooter()
            : {
                  ...prismicContent.data,
                  address: prismicContent.data.address.richText,
                  financial_partner_terms_text:
                      prismicContent.data.financial_partner_terms_text.richText,
              }

    return <Footer hideLinks={hideLinks} locales={locales} content={content} />
}

const query = graphql`
    query Footer {
        allPrismicFooter {
            nodes {
                lang
                data {
                    entity
                    address {
                        richText
                    }
                    terms_body
                    copyright_text
                    financial_partner_terms_text {
                        richText
                    }
                    financial_partner_copyright_text
                    contact_us
                    sales_email
                    info_email
                    support_email
                    product_heading
                    resources_heading
                    company_heading
                    legal_heading
                    solutions_heading
                    partnerships_heading
                    product_links {
                        text
                        link {
                            ...PrismicLink
                        }
                    }
                    resources_links {
                        text
                        link {
                            ...PrismicLink
                        }
                    }
                    company_links {
                        text
                        link {
                            ...PrismicLink
                        }
                    }
                    legal_links {
                        text
                        link {
                            ...PrismicLink
                        }
                    }
                    solutions_links {
                        text
                        link {
                            ...PrismicLink
                        }
                    }
                    partnerships_links {
                        text
                        link {
                            ...PrismicLink
                        }
                    }
                }
            }
        }
    }
`

export default FooterWithQuery
